import PropTypes from 'prop-types';
import React, { useCallback, useMemo, useRef, useEffect, memo } from 'react';
import { Map, List } from 'immutable';

import AgGridTable from '../../helpers/AgGridTable';
import { handleSortModelChanged } from '../../../utils/sorting';
import { EmailBodyRenderer } from '../EmailBodyRenderer';
import { StatusRenderer } from '../StatusRenderer';
import { SentAtRenderer } from '../SentAtRenderer';
import { usePopupContext } from '../../../contexts/PopupContext';

import style from './style.module.css';

const MailingLogsTableComponent = ({ columnDefs, data, onChange, onDoubleClickRow, onGetNextPageData }) => {
  const { openPopup } = usePopupContext();
  const gridApiRef = useRef(null);

  const getRowNodeId = useCallback(row => row.order?.toString(), []);

  const handlePreviewClick = data => {
    openPopup('MailingLogPopup', {
      html: data.html,
      error: data.error,
      hasError: data.hasError,
    });
  };

  const processedColumnDefs = useMemo(
    () =>
      columnDefs.map(columnDef => {
        const field = columnDef.get('field');

        switch (field) {
          case 'createdAt':
            return columnDef.set('cellRendererFramework', SentAtRenderer);

          case 'emailBody':
            return columnDef.set('cellRendererFramework', params => (
              <EmailBodyRenderer data={params.data} onPreviewEmail={() => handlePreviewClick(params.data)} />
            ));

          case 'error':
            return columnDef.set('cellRendererFramework', StatusRenderer);

          default:
            return columnDef.merge({
              autoHeight: true,
            });
        }
      }),
    [],
  );

  useEffect(() => {
    if (gridApiRef.current) {
      gridApiRef.current.resetRowHeights();
    }
  }, [data, gridApiRef.current]);

  const handleGridReady = useCallback(params => {
    gridApiRef.current = params.api;
    params.api.sizeColumnsToFit();
  }, []);

  const handleSort = useCallback(
    sortModel => {
      handleSortModelChanged(sortModel, data, onChange);
    },
    [data, onChange],
  );

  return (
    <div className={style.host}>
      <AgGridTable
        columnDefs={processedColumnDefs}
        getRowNodeId={getRowNodeId}
        isFetching={data.get('isFetching')}
        onGetNextPageData={onGetNextPageData}
        onGridReady={handleGridReady}
        onRowDoubleClicked={onDoubleClickRow}
        onSortModelChanged={handleSort}
        page={data.get('page')}
        rowData={data.get('queryResults')}
        totalPages={data.get('totalPages')}
        sortable
      />
    </div>
  );
};

MailingLogsTableComponent.propTypes = {
  columnDefs: PropTypes.instanceOf(List).isRequired,
  data: PropTypes.instanceOf(Map).isRequired,
  onChange: PropTypes.func.isRequired,
  onGetNextPageData: PropTypes.func.isRequired,
};

export const MailingLogsTable = memo(MailingLogsTableComponent);
